import { createContext, useContext } from 'react';

import { useAppSelector } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { AuthContext } from 'contexts/AuthContext';

import {
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  ROLE_ADMIN,
  ROLE_UNDERWRITER,
  WIP_STATUS_APPEAL_REJECTED,
  WIP_STATUS_APPEAL_SUBMITTED,
  WIP_STATUS_BARA_APPROVED,
  WIP_STATUS_BARA_DECLINED,
  WIP_STATUS_BARA_NEEDS_INFO,
  WIP_STATUS_BARA_SUBMITTED,
  WIP_STATUS_BARA_UNDER_REVIEW,
  WIP_STATUS_EXPIRED,
} from 'variables/constants';

// Wraps the billing section so it stays editable after a BARA approval
export const BaraBillingEditableContext = createContext(false);

const useIsBinderDisabled = (
  formStateKey: 'autoPersonalForm' | 'homePersonalForm' | 'commercialForm',
  isDisabled: boolean = false,
) => {
  const { user } = useContext(AuthContext);
  const isBillingEditable = useContext(BaraBillingEditableContext);
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');

  const signing = useAppSelector((state) => state[formStateKey].signing);
  const wipStatus = useAppSelector((state) =>
    formStateKey === 'commercialForm'
      ? state.commercialForm.wipStatus
      : state[formStateKey].wip_status,
  );
  const baraReferralStatus = useAppSelector((state) =>
    formStateKey === 'commercialForm'
      ? undefined
      : state[formStateKey].bara_referral?.status,
  );

  // Even admins should not be able to edit while signing exists
  if (signing !== null) return true;

  // Even admins should not be able to edit expired policies
  if (wipStatus === WIP_STATUS_EXPIRED) return true;

  const isBaraApproved =
    wipStatus === WIP_STATUS_BARA_APPROVED ||
    baraReferralStatus === BARA_REFERRAL_STATUS_APPROVED;
  const isBaraDeclined =
    wipStatus === WIP_STATUS_BARA_DECLINED ||
    baraReferralStatus === BARA_REFERRAL_STATUS_DECLINED;

  // A final BARA decision locks the form for every role, except billing after approval.
  if (
    isBaraFixesEnabled &&
    (isBaraDeclined || (isBaraApproved && !isBillingEditable))
  )
    return true;

  if (user.role === ROLE_ADMIN) return false;

  if (
    [
      WIP_STATUS_APPEAL_SUBMITTED,
      WIP_STATUS_APPEAL_REJECTED,
      WIP_STATUS_BARA_SUBMITTED,
      WIP_STATUS_BARA_UNDER_REVIEW,
      WIP_STATUS_BARA_NEEDS_INFO,
      ...(isBaraFixesEnabled
        ? []
        : [WIP_STATUS_BARA_APPROVED, WIP_STATUS_BARA_DECLINED]),
    ].includes(wipStatus)
  ) {
    return true;
  }

  if (user.role === ROLE_UNDERWRITER) return true;

  if (isDisabled) return true;

  return false;
};

export default useIsBinderDisabled;
